/**
 * useSunsetBoundary — React hook for sunset-aware Hebrew date toggling.
 *
 * Provides a toggleable sunset boundary state that immediately updates
 * the Hebrew date label in the UI. Uses @triedge/domain-core pure utils.
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 */

import { useCallback, useMemo, useState } from 'react';
import { computeSunsetUtc, type GeoConfig, type SunsetResult } from '@triedge/domain-core';

export type SunsetBoundaryState = {
  /** Current sunset result (UTC time + after-sunset flag). */
  readonly sunset: SunsetResult | null;
  /** Whether the sunset boundary toggle is active. */
  readonly isSunsetApplied: boolean;
  /** Toggle the sunset boundary on/off. */
  readonly toggleSunset: () => void;
  /** Explicitly set the sunset boundary state. */
  readonly setSunsetApplied: (applied: boolean) => void;
};

/**
 * Manage sunset-aware Hebrew date toggle state.
 *
 * @param gregorianDate - ISO date string (YYYY-MM-DD) or null
 * @param geoConfig - Geographic configuration for sunset computation
 * @returns SunsetBoundaryState with toggle controls
 */
export const useSunsetBoundary = (
  gregorianDate: string | null | undefined,
  geoConfig: GeoConfig | undefined
): SunsetBoundaryState => {
  const [manualOverride, setManualOverride] = useState<boolean | null>(null);

  const sunset = useMemo((): SunsetResult | null => {
    if (!gregorianDate || !geoConfig) return null;
    return computeSunsetUtc(gregorianDate, geoConfig);
  }, [gregorianDate, geoConfig?.latitude, geoConfig?.longitude, geoConfig?.timezone]);

  const isSunsetApplied = manualOverride ?? (sunset?.isAfterSunset ?? false);

  const toggleSunset = useCallback(() => {
    setManualOverride((current) => !(current ?? sunset?.isAfterSunset ?? false));
  }, [sunset?.isAfterSunset]);

  const setSunsetApplied = useCallback((applied: boolean) => {
    setManualOverride(applied);
  }, []);

  return {
    sunset,
    isSunsetApplied,
    toggleSunset,
    setSunsetApplied,
  };
};
